import PropTypes from "prop-types";
import toast from "react-hot-toast";
import { addMinutes } from "date-fns";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Box,
  Button,
  Dialog,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { Trash as TrashIcon } from "../../../icons/trash";
import { useAuth } from "../../../hooks/use-auth";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";

export const CalendarEventDialog = (props) => {
  const {
    event,
    onAddComplete,
    onClose,
    onDeleteComplete,
    onEditComplete,
    open,
    range,
    datosPrecargados,
  } = props;
  const { user } = useAuth();
  const router = useRouter();
  const [extras, setExtras] = useState(false);
  const [contactos, setContactos] = useState([]);

  const formik = useFormik({
    initialValues: {
      allDay: false,
      color: "",
      description: "",
      end: addMinutes(new Date(), 30),
      start: new Date(),
      title: "",
      importance: "low",
      submit: null,
      id: "",
      contacto: "",
    },
    validationSchema: Yup.object({
      allDay: Yup.bool(),
      description: Yup.string().max(5000),
      end: Yup.date(),
      start: Yup.date(),
      title: Yup.string()
        .max(255)
        .required("El título del evento es requerido"),
      importance: Yup.string().oneOf(["low", "medium", "high"]),
    }),
    onSubmit: async (values, helpers) => {
      // Si no tiene start o end, agregarle la fecha actual y la fecha actual + 30 minutos
      if (!values.start) {
        values.start = new Date();
      }
      if (!values.end) {
        values.end = addMinutes(new Date(), 30);
      }

      try {
        if (event) {
          //Actualizar el evento
          const success = await GuardarEvento(values);
          if (success) {
            toast.success("Evento actualizado!");
          } else {
            toast.error("Error al actualizar evento");
          }
          onEditComplete();
        } else {
          //Crear el evento
          const success = await GuardarEvento(values);
          if (success) {
            toast.success("Evento agregado!");
          } else {
            toast.error("Error al crear evento");
          }
          onAddComplete();
        }
      } catch (err) {
        toast.error("Error al guardar el evento");
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  //Funcion para traer los contactos del usuario
  const getContactos = async () => {
    const response = await fetch(
      `/api/v1/contactos/traer?userRole=${user?.role}&userId=${user?.uid}&distribuidoraId=${user?.distribuidoraId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const result = await response.json();

    if (result.ok) {
      //Darle formato para el select
      const contactos = result.contactos.map((contacto) => {
        return {
          value: contacto.id,
          label: contacto.name + " " + contacto.surname,
        };
      });
      setContactos(contactos);
    } else {
      toast.error("Error al obtener contactos");
    }
  };

  //Colocar los contactos en el select
  useEffect(() => {
    getContactos();
  }, []);

  //Colocaar los datos precargados en el formulario
  useEffect(() => {
    setExtras(false);
    if (datosPrecargados) {
      formik.setValues({
        description: datosPrecargados.description || "",
        title: datosPrecargados.title || "",
        importance: datosPrecargados.importance || "high",
        submit: null,
        allDay: datosPrecargados.allDay || false,
        contacto: datosPrecargados.contactoId || "",
      });
      if (datosPrecargados.contactoId) {
        setExtras(true);
      }
    }
  }, [datosPrecargados]);

  useEffect(() => {
    setExtras(false);
    if (event !== null || event !== undefined) {
      formik.setValues({
        allDay: event?.allDay || false,
        color: event?.color || "",
        description: event?.description || "",
        end: event?.end ? new Date(event.end) : addMinutes(new Date(), 30),
        start: event?.start ? new Date(event.start) : new Date(),
        title: event?.title || "",
        importance: event?.importance || "low",
        submit: null,
        id: event?.id || "",
        contacto: event?.contactoId || "",
      });
      if (event?.contactoId) {
        setExtras(true);
      }
    }
  }, [event]);

  const GuardarEvento = async (values) => {
    //Hacer peticion POST a la API de calendar
    const response = "";
    if (event) {
      //Editar evento
      const data = {
        allDay: values.allDay ? values.allDay : event?.allDay,
        description: values.description
          ? values.description
          : event?.description,
        end: values.end.getTime() ? values.end.getTime() : event?.end.getTime(),
        start: values.start.getTime()
          ? values.start.getTime()
          : event?.start.getTime(),
        title: values.title ? values.title : event?.title,
        importance: values.importance ? values.importance : event?.importance,
        color:
          values.importance === "low"
            ? "#4caf50"
            : values.importance === "medium"
              ? "#ff9800"
              : "#f44336",
        ownerType: user?.role,
        ownerId: user?.uid,
        id: values.id ? values.id : "",
        contactoId: values.contacto ? values.contacto : "",
      };

      response = await fetch("/api/v1/calendario/evento", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
    } else {
      //Crear evento
      const data = {
        allDay: values.allDay ? values.allDay : false,
        description: values.description ? values.description : "",
        end: values.end.getTime() ? values.end.getTime() : "",
        start: values.start.getTime() ? values.start.getTime() : new Date(),
        title: values.title ? values.title : new Date(),
        importance: values.importance ? values.importance : "low",
        color:
          values.importance === "low"
            ? "#4caf50"
            : values.importance === "medium"
              ? "#ff9800"
              : "#f44336",
        ownerType: user?.role ? user?.role : "",
        ownerId: user?.uid ? user?.uid : "",
        id: values.id ? values.id : "",
        contactoId: values.contacto ? values.contacto : "",
      };

      response = await fetch("/api/v1/calendario/evento", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
    }

    const result = await response.json();
    if (result.ok) {
      return true;
    } else {
      return false;
    }
  };

  const EliminarEvento = async (id) => {
    //Hacer peticion DELETE a la API de calendar

    const data = {
      id: id,
    };

    const response = await fetch("/api/v1/calendario/evento", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();
    if (result.ok) {
      return true;
    } else {
      return false;
    }
  };

  const handleStartDateChange = (date) => {
    formik.setFieldValue("start", date);

    // Prevent end date to be before start date
    if (formik.values.end && date && date > formik.values.end) {
      formik.setFieldValue("end", date);
    }
  };

  const handleEndDateChange = (date) => {
    formik.setFieldValue("end", date);

    // Prevent start date to be after end date
    if (formik.values.start && date && date < formik.values.start) {
      formik.setFieldValue("start", date);
    }
  };

  const handleDelete = async () => {
    try {
      if (!event) {
        return;
      }

      //Eliminar evento
      const success = await EliminarEvento(event?.id);
      if (success) {
        toast.success("Evento eliminado!");
      } else {
        toast.error("Error al eliminar evento");
      }

      onDeleteComplete?.();
    } catch (err) {
      console.error(err);
    }
  };

  const addToGoogleCalendar = async () => {
    // Hacer peticion a /api/v1/calendario/google-calendar para obtener el enlace de invitación a Google Calendar
    const data = {
      title: formik.values.title || "",
      description: formik.values.description || "",
      start: formik.values.start || "",
      end: formik.values.end || "",
      allDay: formik.values.allDay || false,
    };

    const response = await fetch("/api/v1/calendario/google-calendar", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (result.ok) {
      //Abrir el enlace en una nueva pestaña
      window.open(result.link, "_blank");
    } else {
      toast.error("Error al obtener el enlace de invitación a Google Calendar");
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={!!open}>
      <form onSubmit={formik.handleSubmit}>
        <Box sx={{ px: 3, pt: 3 }}>
          <Typography align="center" gutterBottom variant="h5">
            {event ? "Modificar Evento" : "Añadir Evento"}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <TextField
                error={Boolean(formik.touched.title && formik.errors.title)}
                fullWidth
                helperText={formik.touched.title && formik.errors.title}
                label="Título"
                name="title"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.title}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <TextField
                error={Boolean(
                  formik.touched.description && formik.errors.description
                )}
                fullWidth
                helperText={
                  formik.touched.description && formik.errors.description
                }
                label="Descripción"
                name="description"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.description}
                multiline
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Importancia"
                name="importance"
                onChange={formik.handleChange}
                select
                size="small"
                value={formik.values.importance}
                fullWidth
              >
                <MenuItem value="low" key={"low"}>
                  Bajo
                </MenuItem>
                <MenuItem value="medium" key={"medium"}>
                  Medio
                </MenuItem>
                <MenuItem value="high" key={"high"}>
                  Alto
                </MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={extras}
                    name="referencia"
                    onChange={() => {
                      setExtras(!extras);
                      //Limpiar el contacto
                      formik.setFieldValue("contacto", "");
                    }}
                  />
                }
                label="Agregar Contacto"
              />
            </Grid>

            {extras && (
              <>
                <Grid item xs={12} md={12}>
                  <TextField
                    error={Boolean(
                      formik.touched.contacto && formik.errors.contacto
                    )}
                    fullWidth
                    helperText={
                      formik.touched.contacto && formik.errors.contacto
                    }
                    label="Contacto"
                    name="contacto"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.contacto}
                    select
                  >
                    {contactos.length === 0 ? (
                      <MenuItem value={"empty"} key={"empty"}>
                        Aun no tienes contactos
                      </MenuItem>
                    ) : (
                      contactos.map((contacto) => (
                        <MenuItem value={contacto.value} key={contacto.value}>
                          {contacto.label}
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                </Grid>
              </>
            )}
            <Grid item xs={12} md={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.allDay}
                    name="allDay"
                    onChange={formik.handleChange}
                  />
                }
                label="Este evento durará todo el día"
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <DateTimePicker
                label="Inicio"
                onChange={handleStartDateChange}
                renderInput={(inputProps) => (
                  <TextField fullWidth {...inputProps} />
                )}
                value={formik.values.start}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <DateTimePicker
                label="Fin"
                onChange={handleEndDateChange}
                renderInput={(inputProps) => (
                  <TextField fullWidth {...inputProps} />
                )}
                value={formik.values.end}
              />
            </Grid>
            {Boolean(formik.touched.end && formik.errors.end) && (
              <Grid item xs={12} md={12}>
                <FormHelperText error>{formik.errors.end}</FormHelperText>
              </Grid>
            )}
            <Grid item xs={12} md={12}>
              <Typography variant="caption">
                No olvides modificar la fecha de inicio y fin si es necesario
              </Typography>
            </Grid>
          </Grid>
        </Box>
        <Divider />
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            p: 2,
          }}
        >
          {event && (
            <>
              <IconButton onClick={() => handleDelete()}>
                <TrashIcon fontSize="small" />
              </IconButton>

              <Button
                sx={{ ml: 1 }}
                onClick={() => addToGoogleCalendar()}
                variant="outlined"
              >
                Agregar a Google Calendar
              </Button>
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            disabled={formik.isSubmitting}
            sx={{ ml: 1 }}
            type="submit"
            variant="contained"
          >
            Confirmar
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

CalendarEventDialog.propTypes = {
  event: PropTypes.object,
  onAddComplete: PropTypes.func,
  onClose: PropTypes.func,
  onDeleteComplete: PropTypes.func,
  onEditComplete: PropTypes.func,
  open: PropTypes.bool,
  range: PropTypes.object,
};
